import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { from, of, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { fetchAuthSession, fetchUserAttributes } from '@aws-amplify/auth';
import { CognitoUserService } from '@features/auth/services';
import { PendingEmailConfirmationService } from '@features/auth-amplify/services/pending-email-confirmation.service';

import { AuthAmplifyActions } from '../actions';
import { mapCognitoAttributes } from '@features/auth-amplify/utils';
import { UserActions } from '@features/auth/state/actions';

@Injectable({ providedIn: 'root' })
export class SessionEffects {
  public initRehydrate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType('@ngrx/effects/init'),
      map(() => AuthAmplifyActions.sessionRehydrateInitiated())
    )
  );

  public performRehydrate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthAmplifyActions.sessionRehydrateInitiated),
      switchMap(() => {
        if (this.pendingSvc.isPending()) {
          return of(AuthAmplifyActions.sessionRehydrateFailed({ payload: 'pending_confirmation' }));
        }
        // Respect legacy sliding expiry (EXPIRE_HOURS). If expired, abort rehydrate so user is logged out as before.
        let expired = false;
        try {
          const valid = this.cognitoUserService.checkExpiry();
          expired = !valid;
        } catch {
          expired = true; // fail safe: treat as expired
        }
        if (expired) {
          return of(AuthAmplifyActions.sessionRehydrateFailed({ payload: 'expired' }));
        }
        return from(fetchAuthSession()).pipe(
          switchMap((session: any) => {
            const signedIn = !!session?.tokens?.idToken;
            if (!signedIn) {
              return of(AuthAmplifyActions.sessionRehydrateFailed({ payload: 'no_session' }));
            }
            return from(fetchUserAttributes()).pipe(
              catchError(() => of({} as any)),
              map((attrs: any) => mapCognitoAttributes(attrs || {})),
              map((user) => AuthAmplifyActions.sessionRehydrateSuccess({ payload: user }))
            );
          }),
          catchError((err) => of(AuthAmplifyActions.sessionRehydrateFailed({ payload: err })))
        );
      })
    )
  );

  public expiredRedirect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthAmplifyActions.sessionRehydrateFailed),
      map((a: any) => a?.payload),
      // Only treat explicit 'expired' sentinel value as session expiry
      switchMap((payload) => (payload === 'expired' ? of(UserActions.sessionExpiredDetected()) : of()))
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly cognitoUserService: CognitoUserService,
    private readonly pendingSvc: PendingEmailConfirmationService
  ) {}
}
